/**
 * BoundaryVisualizer
 *
 * Draws visual boundaries on the left and right edges of the game world
 * to indicate where the bounce-back mechanic activates.
 * Changes appearance based on current zone theme.
 */

import Phaser from 'phaser';
import { getZoneIdFromY } from '../level/ZoneThemes';

export class BoundaryVisualizer {
  private scene: Phaser.Scene;
  private leftWall: Phaser.GameObjects.Graphics;
  private rightWall: Phaser.GameObjects.Graphics;
  private worldWidth: number = 800;
  private worldHeight: number;
  private currentZone: number = 1;
  private lastCameraY: number = 0; // Track camera position for efficient updates

  private readonly WALL_WIDTH = 20; // Width of visual boundary
  private readonly RENDER_THRESHOLD = 100; // Only re-render if camera moved more than this

  constructor(scene: Phaser.Scene, worldHeight: number) {
    this.scene = scene;
    this.worldHeight = worldHeight;

    // Create graphics objects for walls
    this.leftWall = scene.add.graphics();
    this.leftWall.setDepth(5); // Behind Blobo (10) but in front of background
    this.leftWall.setScrollFactor(1, 1); // Follow camera

    this.rightWall = scene.add.graphics();
    this.rightWall.setDepth(5);
    this.rightWall.setScrollFactor(1, 1);

    // Initial render
    this.render(0);
  }

  /**
   * Update walls based on camera position (zone detection)
   * PERFORMANCE: Only re-render if camera moved significantly or zone changed
   */
  public update(cameraY: number): void {
    // Convert camera Y to world Y for zone detection
    const worldY = this.worldHeight - cameraY;
    const newZone = getZoneIdFromY(worldY);

    // Check if camera moved significantly
    const cameraMoved = Math.abs(cameraY - this.lastCameraY) > this.RENDER_THRESHOLD;
    const zoneChanged = newZone !== this.currentZone;

    // Re-render if zone changed OR camera moved significantly
    if (zoneChanged || cameraMoved) {
      this.currentZone = newZone;
      this.lastCameraY = cameraY;
      this.render(cameraY);
    }
  }

  /**
   * Render the boundary walls based on current zone
   * PERFORMANCE FIX: Only render viewport-sized walls instead of full 11,500px height
   */
  private render(cameraY: number): void {
    this.leftWall.clear();
    this.rightWall.clear();

    // Get zone-specific appearance
    const appearance = this.getZoneAppearance(this.currentZone);

    // PERFORMANCE: Only draw what's visible around the camera
    const cameraHeight = this.scene.cameras.main.height;
    const buffer = 500; // Buffer above/below for smooth scrolling
    const startY = Math.max(0, cameraY - buffer);
    const endY = Math.min(this.worldHeight, cameraY + cameraHeight + buffer);
    const renderHeight = endY - startY;

    // Draw left wall (only visible portion in world coordinates)
    this.drawWall(this.leftWall, 0, appearance, startY, renderHeight);

    // Draw right wall (only visible portion in world coordinates)
    this.drawWall(
      this.rightWall,
      this.worldWidth - this.WALL_WIDTH,
      appearance,
      startY,
      renderHeight
    );
  }

  /**
   * Draw a single wall with zone-themed appearance
   * PERFORMANCE FIX: Only draw visible portion
   */
  private drawWall(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    appearance: { base: number; dark: number; pattern: string },
    startY: number,
    height: number
  ): void {
    // Base wall color (solid fill) - only visible portion
    graphics.fillStyle(appearance.base, 0.3); // Semi-transparent
    graphics.fillRect(x, startY, this.WALL_WIDTH, height);

    // Darker edge (gives depth) - only visible portion
    graphics.fillStyle(appearance.dark, 0.5);
    const edgeWidth = 3;
    if (x === 0) {
      // Left wall - darker on left edge
      graphics.fillRect(x, startY, edgeWidth, height);
    } else {
      // Right wall - darker on right edge
      graphics.fillRect(x + this.WALL_WIDTH - edgeWidth, startY, edgeWidth, height);
    }

    // Add pattern based on zone - only in visible area
    this.drawPattern(graphics, x, appearance.pattern, startY, height);
  }

  /**
   * Draw zone-specific patterns on the wall
   * PERFORMANCE FIX: Only draw patterns in visible viewport
   */
  private drawPattern(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    pattern: string,
    startY: number,
    height: number
  ): void {
    // PERFORMANCE FIX: Only draw patterns in the visible range
    const spacing = 500; // Large spacing between patterns
    const endY = startY + height;

    // Calculate which pattern indices are visible
    const firstPatternIndex = Math.floor(startY / spacing);
    const lastPatternIndex = Math.ceil(endY / spacing);
    const count = Math.min(lastPatternIndex - firstPatternIndex + 1, 5); // Max 5 patterns in viewport

    switch (pattern) {
      case 'bark':
        // Tree bark lines (Zone 1-2)
        graphics.lineStyle(2, 0x3d2817, 0.4);
        for (let i = 0; i < count; i++) {
          const patternIndex = firstPatternIndex + i;
          const y = patternIndex * spacing + spacing * 0.5;
          graphics.lineBetween(x, y, x + this.WALL_WIDTH, y + 10);
          graphics.lineBetween(x + 5, y + 20, x + this.WALL_WIDTH - 5, y + 35);
        }
        break;

      case 'vines':
        // Hanging vines (Zone 2-3) - Simplified for performance
        graphics.lineStyle(3, 0x2d5016, 0.5);
        for (let i = 0; i < count; i++) {
          const patternIndex = firstPatternIndex + i;
          const y = patternIndex * spacing;
          const vineX = x + this.WALL_WIDTH / 2;
          // Simple vertical line instead of wavy (performance)
          graphics.lineBetween(vineX, y, vineX + 2, y + 60);
        }
        break;

      case 'stone':
        // Stone blocks (Zone 4-5) - Simplified for performance
        graphics.lineStyle(2, 0x1a1a2e, 0.6);
        for (let i = 0; i < count; i++) {
          const patternIndex = firstPatternIndex + i;
          const y = patternIndex * spacing;
          // Simple horizontal lines instead of full blocks
          graphics.lineBetween(x, y, x + this.WALL_WIDTH, y);
        }
        break;

      case 'energy':
        // Energy pulses (Zone 6) - Simplified for performance
        graphics.lineStyle(2, 0x00ffff, 0.7);
        for (let i = 0; i < count; i++) {
          const patternIndex = firstPatternIndex + i;
          const y = patternIndex * spacing;
          const centerX = x + this.WALL_WIDTH / 2;
          // Single circle instead of two (performance)
          graphics.strokeCircle(centerX, y, 6);
        }
        break;

      case 'clouds':
        // Cloud wisps (Zone 3) - Simplified for performance
        graphics.fillStyle(0xffffff, 0.2);
        for (let i = 0; i < count; i++) {
          const patternIndex = firstPatternIndex + i;
          const y = patternIndex * spacing;
          const cloudX = x + this.WALL_WIDTH / 2;
          // Single ellipse instead of three (performance)
          graphics.fillEllipse(cloudX, y, 15, 8);
        }
        break;

      case 'gold':
        // Golden ornaments (Zone 5)
        graphics.lineStyle(3, 0xffd700, 0.6);
        for (let i = 0; i < count; i++) {
          const patternIndex = firstPatternIndex + i;
          const y = patternIndex * spacing + spacing * 0.5;
          const centerX = x + this.WALL_WIDTH / 2;
          // Diamond shapes
          graphics.beginPath();
          graphics.moveTo(centerX, y - 8);
          graphics.lineTo(centerX + 6, y);
          graphics.lineTo(centerX, y + 8);
          graphics.lineTo(centerX - 6, y);
          graphics.closePath();
          graphics.strokePath();
        }
        break;

      default:
        // No pattern
        break;
    }
  }

  /**
   * Get visual appearance settings for each zone
   */
  private getZoneAppearance(zoneId: number): { base: number; dark: number; pattern: string } {
    switch (zoneId) {
      case 1: // Forest Floor
        return {
          base: 0x2d4a3e, // Dark forest green
          dark: 0x1a2a26, // Very dark green
          pattern: 'bark',
        };

      case 2: // Tree Canopy
        return {
          base: 0x5a7a4a, // Olive green
          dark: 0x3d5030, // Dark olive
          pattern: 'vines',
        };

      case 3: // Sky Realm
        return {
          base: 0x5a7a9d, // Sky blue
          dark: 0x3d4d6a, // Dark blue
          pattern: 'clouds',
        };

      case 4: // Starlit Night
        return {
          base: 0x3d2e5f, // Deep purple
          dark: 0x261d3d, // Very dark purple
          pattern: 'stone',
        };

      case 5: // Golden Summit
        return {
          base: 0x8d7330, // Dark gold
          dark: 0x5a4a20, // Very dark gold
          pattern: 'gold',
        };

      case 6: // Ultimate Challenge
        return {
          base: 0xb87700, // Dark amber
          dark: 0x7a4d00, // Very dark amber
          pattern: 'energy',
        };

      default:
        return {
          base: 0x808080,
          dark: 0x404040,
          pattern: 'none',
        };
    }
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.leftWall.destroy();
    this.rightWall.destroy();
  }
}
